import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { bcStates } from 'common/common/states/index';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { DepartmentDropdownItem } from './models/dropdown-item.interface';
import { queueViewTabConfig } from './queue-view-tab/models/tab-config';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { MatSelectChange } from '@angular/material/select';
import { CoolLocalStorage } from '@angular-cool/storage';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { QueueViewNotificationsService } from './queue-view-notifications.service';
import { BcConfigService } from 'common/config/provider';
import { Settings } from 'common/settings/settings.interface';
import { BcRestApi } from 'common/api/rest-api';
import { TranslateService } from '@ngx-translate/core';
import { TabSettings } from 'components/queue-view/live-workload-tab/interfaces/tab-settings';
import { QueueViewTabComponent } from 'components/queue-view/queue-view-tab/queue-view-tab.component';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { AccountPermissionType } from 'common/common/constants/account-permission-type';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Component({
  selector: 'app-queue-view',
  templateUrl: './queue-view.component.html',
  styleUrls: ['./queue-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class QueueViewComponent implements OnInit, OnDestroy {
  @ViewChild(QueueViewTabComponent)
  queueViewTabComponent: QueueViewTabComponent;

  public departmentList: DepartmentDropdownItem[] = [];
  public selectedDepartments: DepartmentDropdownItem[] = [];
  public tabs = [];
  public currentTab;
  public areDepartmentsLoading: boolean = false;

  private _temporarySelectedDepartments: DepartmentDropdownItem[] = [];
  private _startTime;
  private _hasNanorepIntegration: boolean = false;
  private _hasAgentListFeature: boolean = false;

  constructor(private stateService: StateServiceWrapper,
              private bcRestApi: BcRestApi,
              private bcAnalytics: BcAnalytics,
              private changeDetector: ChangeDetectorRef,
              private _settingsService: BcSettingsService,
              private _localStorageService: CoolLocalStorage,
              private _queueViewNotificationsService: QueueViewNotificationsService,
              private _userPermissionService: UserPermissionService,
              private _bcConfig: BcConfigService,
              private _translateService: TranslateService,
              private _commonAuth: CommonAuthService) {
  }

  get isDepartmentSelectorVisible(): boolean {
    return this.currentTab ? this.currentTab.hasDepartmentSelector && this.departmentList.length !== 0 : false;
  }

  async ngOnInit(): Promise<void> {
    if (await this._commonAuth.isBoldEnabled()) {
      this._loadTabs();

      this._startTime = new Date().getTime();
      this._loadDepartments();
    }
    this._queueViewNotificationsService.initNotificationSubscriptions();
    this._queueViewNotificationsService.forceDisableRedDots = true;
  }

  ngOnDestroy(): void {
    const elapsedSecs = (new Date().getTime() - this._startTime) / 1000;
    this.bcAnalytics.eventTrack('QueueViewClosed', { duration: elapsedSecs });
    this._queueViewNotificationsService.clearAllSubscriptions();
  }

  onOpenedStateChange(isOpened: boolean): void {
    if (!isOpened) {
      this.selectedDepartments = this._temporarySelectedDepartments;
    }
  }

  onSelectionChange(selectedDepartments: MatSelectChange): void {
    this._temporarySelectedDepartments = selectedDepartments.value;
  }

  private async _loadTabs(): Promise<void> {
    await this._getChatBotFeatureValue();
    await this._getAgentListFeatureValue();

    this.tabs = [queueViewTabConfig.QUEUE, queueViewTabConfig.ASSIGNED, queueViewTabConfig.CLOSED];

    if (this._hasNanorepIntegration) {
      this.tabs.unshift(queueViewTabConfig.CHATBOT);
    }

    if (this._isLiveWorkloadTabEnabled()) {
      this.tabs.push(queueViewTabConfig.LIVE_WORKLOAD);
    }

    const selectedTabId = this._localStorageService.getItem(bcSessionKeys.storage.queueViewSelectedTabId);
    this.currentTab = selectedTabId ? this.tabs.find(tab => tab.id === selectedTabId) : this.tabs[0];
    this.changeDetector.detectChanges();
  }

  private _getChatBotFeatureValue() {
    this._settingsService.getSettings().then(settings => {
      this._hasNanorepIntegration = this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_INTEGRATIONS_NANOREP);
    });
  }

  private async _loadDepartments(): Promise<void> {
    this.areDepartmentsLoading = true;
    const loadedDepartments = await this.bcRestApi.getDepartmentsWithOperators();
    this.departmentList = loadedDepartments
      .filter(department => department.name);
    const noDepartments = loadedDepartments.find(department => !department.name && department.id === '0');
    if (noDepartments) {
      noDepartments.name = this._translateService.instant('queueView.departmentDropdown.noDepartments');
      this.departmentList.unshift(noDepartments);
    }
    this.selectedDepartments = this._temporarySelectedDepartments = this.departmentList.slice(0);
    this.areDepartmentsLoading = false;
    this.changeDetector.detectChanges();
  }

  public navigateToWorkspace(): void {
    this.stateService.go(bcStates.layout.url);
    this._queueViewNotificationsService.clearAllSubscriptions();
    this._queueViewNotificationsService.forceDisableRedDots = true;
  }

  toggleAllDepartments() {
    if (this.selectedDepartments.length === this.departmentList.length) {
      this.selectedDepartments = this._temporarySelectedDepartments = [];
    } else {
      this.selectedDepartments = this._temporarySelectedDepartments = this.departmentList.slice(0);
    }
    this.changeDetector.detectChanges();
  }

  public onNavigate(tabSettings: TabSettings): void {
    if (tabSettings.filterDepartmentIds) {
      this.onFilterDepartments(tabSettings.filterDepartmentIds);
    } else {
      this.selectedDepartments = this._temporarySelectedDepartments = this.departmentList.slice(0);
    }
    this._localStorageService.setItem(bcSessionKeys.storage.queueViewSelectedChannelType, tabSettings.workItemType);
    this._localStorageService.removeItem(bcSessionKeys.storage.queueViewSelectedFolderId + 'assigned' + '-' + tabSettings.workItemType.toLowerCase());

    this.changeTab(queueViewTabConfig[tabSettings.tab]);

    this.queueViewTabComponent.setFilterGrid(tabSettings.operatorName);
    this.queueViewTabComponent.setSelectedFoldersToAll();
  }

  public changeTab(tab: any): void {
    this.currentTab = tab;
    this._localStorageService.setItem(bcSessionKeys.storage.queueViewSelectedTabId, this.currentTab.id);
    this.changeDetector.detectChanges();
  }

  public getUnansweredMessageCount(): number {
    return this._queueViewNotificationsService.forceDisableRedDots ? 0 : 1;
  }

  public isLiveWorkloadTabActive(tabId: string): boolean {
    return tabId === queueViewTabConfig.LIVE_WORKLOAD.id;
  }

  private _isLiveWorkloadTabEnabled(): boolean {
    return this._bcConfig.current.features.liveWorkloadMonitorView && this._hasAgentListFeature && this._userPermissionService.hasAccountPermissionFor(AccountPermissionType.AgentList);
  }

  private async _getAgentListFeatureValue(): Promise<void> {
    const settings: Settings = await this._settingsService.getSettings();
    // BcFeatures.LC_AGENT_LIST is used as feature switch for Live Workload tab
    this._hasAgentListFeature = this._settingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_AGENT_LIST);
  }

  public onFilterDepartments(departmentIds: string[]): void {
    this.selectedDepartments = this.departmentList.filter(department => departmentIds.includes(department.id));
  }
}
